// Thin client for the Cloudflare gateway (api.newaxis.net).
//
// Everything here is reachable from mainland China — no Google endpoints.
// The session token is kept in localStorage and also set as an HttpOnly cookie
// by the API, so media URLs can carry it explicitly when the site is served
// from a different registrable domain (e.g. the *.web.app staging URL).

export const API_BASE = (process.env.REACT_APP_API_BASE || 'https://api.newaxis.net').replace(/\/+$/, '');

const TOKEN_KEY = 'na_token';

export const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
};

export const setToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
};

async function request(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `http_${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  me: () => request('/api/auth/me'),
  login: async (username, password) => {
    const data = await request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    if (data.token) setToken(data.token);
    return data;
  },
  logout: async () => {
    try { await request('/api/auth/logout', { method: 'POST' }); } finally { setToken(''); }
  },
  changePassword: (currentPassword, newPassword) => request('/api/auth/password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  }),
  content: () => request('/api/content'),
  saveContent: (sections, nav) => request('/api/content', {
    method: 'PUT',
    body: JSON.stringify({ sections, nav }),
  }),
  videos: () => request('/api/videos'),
  createVideo: (video) => request('/api/videos', {
    method: 'POST',
    body: JSON.stringify(video),
  }),
  deleteVideo: (id) => request(`/api/videos/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  uploadUrl: (filename, contentType, prefix = 'videos') => request('/api/admin/upload-url', {
    method: 'POST',
    body: JSON.stringify({ filename, contentType, prefix }),
  }),
  adminUsers: () => request('/api/admin/users'),
  adminCreateUsers: (users) => request('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify({ users }),
  }),
  adminDeleteUser: (username) => request('/api/admin/users/delete', {
    method: 'POST',
    body: JSON.stringify({ username }),
  }),
  putFile: (url, file, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('上传失败 ' + xhr.status)));
    xhr.onerror = () => reject(new Error('上传失败（网络错误）'));
    xhr.send(file);
  }),
  mediaUrl: (key) => {
    const token = getToken();
    const path = String(key).split('/').map(encodeURIComponent).join('/');
    return `${API_BASE}/api/media/${path}${token ? `?t=${encodeURIComponent(token)}` : ''}`;
  },
};
